const { ENCRYPTION } = require("./secureConfig");

// ---------- Encoding helpers ----------
function utf8Encode(str) {
  return new TextEncoder().encode(str);
}

function utf8Decode(bytes) {
  return new TextDecoder(ENCRYPTION.textEncoding).decode(bytes);
}

function bytesToBase64(bytes) {
  if (!(bytes instanceof Uint8Array)) bytes = new Uint8Array(bytes);
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  return typeof btoa !== "undefined"
    ? btoa(binary)
    : Buffer.from(binary, "binary").toString("base64");
}

function base64ToBytes(base64) {
  const binary =
    typeof atob !== "undefined"
      ? atob(base64)
      : Buffer.from(base64, "base64").toString("binary");
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---------- CryptoKey helpers ----------
async function importAesGcmKeyFromBase64(keyBase64) {
  console.log(
    "[DEBUG] importAesGcmKeyFromBase64 called with key length:",
    keyBase64?.length
  );
  console.log("[DEBUG] Key preview:", keyBase64?.substring(0, 20) + "...");

  try {
    console.log("[DEBUG] Converting base64 to bytes");
    const raw = base64ToBytes(keyBase64);
    console.log("[DEBUG] Raw bytes length:", raw.length);
    console.log(
      "[DEBUG] Expected key length (bits):",
      ENCRYPTION.keyLengthBits
    );
    console.log(
      "[DEBUG] Expected key length (bytes):",
      ENCRYPTION.keyLengthBits / 8
    );

    console.log("[DEBUG] Calling crypto.subtle.importKey");
    console.log("[DEBUG] Import parameters:", {
      format: "raw",
      keyData: raw,
      algorithm: {
        name: ENCRYPTION.algorithm,
        length: ENCRYPTION.keyLengthBits,
      },
      extractable: false,
      keyUsages: ["encrypt", "decrypt"],
    });

    const key = await crypto.subtle.importKey(
      "raw",
      raw,
      { name: ENCRYPTION.algorithm, length: ENCRYPTION.keyLengthBits },
      false,
      ["encrypt", "decrypt"]
    );
    console.log("[DEBUG] Key imported successfully");
    return key;
  } catch (importError) {
    console.error("[DEBUG] Key import failed:", importError);
    console.error("[DEBUG] Import error details:", {
      message: importError.message,
      name: importError.name,
      stack: importError.stack,
    });
    throw importError;
  }
}

async function exportKeyToBase64(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return bytesToBase64(new Uint8Array(raw));
}

// ---------- AES-GCM message encryption ----------
async function encryptMessage(plaintext, keyBase64) {
  console.log("[DEBUG] encryptMessage called with:", {
    plaintextLength: plaintext?.length,
    keyLength: keyBase64?.length,
  });

  if (!plaintext && plaintext !== "")
    throw new Error("encryptMessage: plaintext is required");
  if (!keyBase64) throw new Error("encryptMessage: keyBase64 is required");

  console.log("[DEBUG] Input validation passed");
  console.log("[DEBUG] Plaintext:", plaintext);
  console.log("[DEBUG] Key preview:", keyBase64.substring(0, 20) + "...");

  try {
    console.log("[DEBUG] Importing AES-GCM key from base64");
    const key = await importAesGcmKeyFromBase64(keyBase64);
    console.log("[DEBUG] Key imported successfully");

    console.log("[DEBUG] Generating random IV");
    const iv = crypto.getRandomValues(new Uint8Array(ENCRYPTION.ivLengthBytes));
    console.log("[DEBUG] IV generated, length:", iv.length);

    console.log("[DEBUG] Encoding plaintext to UTF-8");
    const data = utf8Encode(plaintext);
    console.log("[DEBUG] Data encoded, length:", data.length);

    console.log("[DEBUG] Starting encryption with WebCrypto");
    // tagLength defaults to 128 bits; returned ciphertext includes the tag appended
    const encrypted = await crypto.subtle.encrypt(
      { name: ENCRYPTION.algorithm, iv },
      key,
      data
    );
    console.log("[DEBUG] Encryption completed");

    const encryptedBytes = new Uint8Array(encrypted);
    console.log("[DEBUG] Encrypted bytes length:", encryptedBytes.length);

    // Split auth tag (last 16 bytes = 128 bits)
    const tagLengthBytes = 16;
    if (encryptedBytes.length < tagLengthBytes)
      throw new Error("encryptMessage: ciphertext too short");

    console.log("[DEBUG] Splitting auth tag and ciphertext");
    const authTagBytes = encryptedBytes.slice(
      encryptedBytes.length - tagLengthBytes
    );
    const cipherBytes = encryptedBytes.slice(
      0,
      encryptedBytes.length - tagLengthBytes
    );

    console.log("[DEBUG] Auth tag length:", authTagBytes.length);
    console.log("[DEBUG] Cipher bytes length:", cipherBytes.length);

    const result = {
      ciphertext: bytesToBase64(cipherBytes),
      iv: bytesToBase64(iv),
      authTag: bytesToBase64(authTagBytes),
    };

    console.log("[DEBUG] Encryption result:", {
      ciphertextLength: result.ciphertext.length,
      ivLength: result.iv.length,
      authTagLength: result.authTag.length,
    });

    return result;
  } catch (encryptError) {
    console.error("[DEBUG] Encryption process failed:", encryptError);
    console.error("[DEBUG] Error details:", {
      message: encryptError.message,
      name: encryptError.name,
      stack: encryptError.stack,
    });
    throw encryptError;
  }
}

async function decryptMessage(
  ciphertextBase64,
  keyBase64,
  ivBase64,
  authTagBase64
) {
  if (!ciphertextBase64 || !keyBase64 || !ivBase64 || !authTagBase64) {
    throw new Error(
      "decryptMessage: ciphertext, keyBase64, ivBase64 and authTagBase64 are required"
    );
  }
  const key = await importAesGcmKeyFromBase64(keyBase64);
  const iv = base64ToBytes(ivBase64);
  const cipherBytes = base64ToBytes(ciphertextBase64);
  const authTagBytes = base64ToBytes(authTagBase64);

  // Reconstruct WebCrypto expected input: ciphertext || authTag
  const combined = new Uint8Array(cipherBytes.length + authTagBytes.length);
  combined.set(cipherBytes, 0);
  combined.set(authTagBytes, cipherBytes.length);

  const decrypted = await crypto.subtle.decrypt(
    { name: ENCRYPTION.algorithm, iv },
    key,
    combined
  );
  return utf8Decode(new Uint8Array(decrypted));
}

module.exports = {
  utf8Encode,
  utf8Decode,
  bytesToBase64,
  base64ToBytes,
  importAesGcmKeyFromBase64,
  exportKeyToBase64,
  encryptMessage,
  decryptMessage,
};
